import { ArrowDown02Icon, ArrowLeft01Icon, ArrowRight01Icon, ArrowUp02Icon, ArrowUpDownIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Button } from '../primitives/button.js';
import { Checkbox } from '../primitives/checkbox.js';
import { Select } from '../primitives/select.js';
import { EmptyState, Skeleton } from '../primitives/states.js';

export type SortDirection = 'asc' | 'desc';
export interface SortState {
  columnId: string;
  direction: SortDirection;
}

export interface DataTableColumn<T> {
  id: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  /** Server-side sortable: clicking calls onSortChange; the table never sorts locally. */
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  /** Numeric columns use tabular numerals and right alignment by default. */
  numeric?: boolean;
  /** Tailwind width classes for the column, e.g. "w-40" or "min-w-48". */
  className?: string;
  /** Hide below the md breakpoint to keep dense tables readable on phones. */
  hideOnMobile?: boolean;
}

export interface DataTableSelection {
  selectedIds: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
}

export interface DataTableProps<T> {
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  getRowId: (row: T) => string;
  /** Accessible caption (visually hidden unless showCaption). */
  caption: string;
  showCaption?: boolean;
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  /** Shows skeleton rows when there are no rows yet; dims rows while refreshing. */
  loading?: boolean;
  skeletonRows?: number;
  /** Rendered when not loading and rows is empty. */
  empty?: React.ReactNode;
  /** Replaces the body (e.g. an ErrorState or PermissionDenied). */
  error?: React.ReactNode;
  selection?: DataTableSelection;
  /** Makes the first cell a button and the row clickable. */
  onRowClick?: (row: T) => void;
  /** Accessible label for the row action, e.g. row => `Abrir ${row.name}`. */
  rowActionLabel?: (row: T) => string;
  /** Highlights a row (e.g. the one open in a side panel). */
  activeRowId?: string | null;
  /** Max height (CSS length) of the scroll area; the header stays sticky. */
  maxHeight?: string;
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  density?: 'compact' | 'comfortable';
  className?: string;
}

/** Dense, accessible data table with sticky header, skeleton loading, empty/error states and row actions. */
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  caption,
  showCaption,
  sort,
  onSortChange,
  loading,
  skeletonRows = 5,
  empty,
  error,
  selection,
  onRowClick,
  rowActionLabel,
  activeRowId,
  maxHeight,
  toolbar,
  footer,
  density = 'compact',
  className,
}: DataTableProps<T>) {
  const cellPad = density === 'compact' ? 'px-3 py-2.5' : 'px-4 py-3.5';
  const allIds = rows.map(getRowId);
  const selectedCount = selection ? allIds.filter((id) => selection.selectedIds.has(id)).length : 0;
  const headerChecked = selectedCount === 0 ? false : selectedCount === allIds.length ? true : 'indeterminate';
  const colCount = columns.length + (selection ? 1 : 0);
  const mobileHide = (c: DataTableColumn<T>) => (c.hideOnMobile ? 'hidden md:table-cell' : undefined);

  const toggleSort = (col: DataTableColumn<T>) => {
    if (!onSortChange) return;
    const direction: SortDirection = sort?.columnId === col.id && sort.direction === 'desc' ? 'asc' : 'desc';
    onSortChange({ columnId: col.id, direction });
  };

  return (
    <div className={cn('min-w-0', className)}>
      {toolbar ? <div className="mb-3">{toolbar}</div> : null}
      <div
        className="relative overflow-auto rounded-card border border-border-subtle bg-surface-default"
        style={maxHeight ? { maxHeight } : undefined}
        tabIndex={0}
        role="region"
        aria-label={caption}
      >
        <table className="w-full border-collapse text-left text-body" aria-busy={loading || undefined}>
          <caption className={showCaption ? 'px-4 py-2 text-left text-meta text-text-secondary' : 'sr-only'}>{caption}</caption>
          <thead>
            <tr>
              {selection ? (
                <th scope="col" className={cn(cellPad, 'sticky top-0 z-[2] w-10 border-b border-border-subtle bg-surface-subtle')}>
                  <Checkbox
                    aria-label="Seleccionar todas las filas"
                    checked={headerChecked}
                    onCheckedChange={(c) => selection.onChange(c === true ? new Set(allIds) : new Set())}
                  />
                </th>
              ) : null}
              {columns.map((col) => {
                const active = sort?.columnId === col.id;
                const ariaSort = active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : col.sortable ? 'none' : undefined;
                const align = col.align ?? (col.numeric ? 'right' : 'left');
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={ariaSort}
                    className={cn(
                      cellPad,
                      'sticky top-0 z-[2] whitespace-nowrap border-b border-border-subtle bg-surface-subtle text-meta font-semibold text-text-secondary',
                      align === 'right' && 'text-right',
                      align === 'center' && 'text-center',
                      mobileHide(col),
                      col.className,
                    )}
                  >
                    {col.sortable && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col)}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-[4px] hover:text-text-primary focus-visible:outline-2 focus-visible:outline-focus',
                          active && 'text-text-primary',
                          align === 'right' && 'flex-row-reverse',
                        )}
                      >
                        {col.header}
                        {active ? (
                          sort.direction === 'asc' ? (
                            <Icon icon={ArrowUp02Icon} aria-hidden className="size-3.5" />
                          ) : (
                            <Icon icon={ArrowDown02Icon} aria-hidden className="size-3.5" />
                          )
                        ) : (
                          <Icon icon={ArrowUpDownIcon} aria-hidden className="size-3.5 opacity-60" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td colSpan={colCount}>{error}</td>
              </tr>
            ) : null}
            {!error && loading && rows.length === 0
              ? Array.from({ length: skeletonRows }, (_, i) => (
                  <tr key={`sk-${i}`} className="border-b border-border-subtle last:border-b-0">
                    {selection ? (
                      <td className={cellPad}>
                        <Skeleton className="size-5" />
                      </td>
                    ) : null}
                    {columns.map((col, j) => (
                      <td key={col.id} className={cn(cellPad, mobileHide(col))}>
                        <Skeleton className={cn('h-4', j === 0 ? 'w-40' : 'w-24', col.numeric && 'ml-auto w-16')} />
                      </td>
                    ))}
                  </tr>
                ))
              : null}
            {!error && loading && rows.length === 0 ? (
              <tr className="sr-only">
                <td colSpan={colCount} role="status">
                  Cargando…
                </td>
              </tr>
            ) : null}
            {!error && !loading && rows.length === 0 ? (
              <tr>
                <td colSpan={colCount}>{empty ?? <EmptyState compact title="Sin resultados" description="Ajusta los filtros para ver datos." />}</td>
              </tr>
            ) : null}
            {!error &&
              rows.map((row) => {
                const id = getRowId(row);
                const selected = selection?.selectedIds.has(id) ?? false;
                const isActive = activeRowId === id;
                return (
                  <tr
                    key={id}
                    aria-selected={selection ? selected : undefined}
                    aria-current={isActive || undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      'border-b border-border-subtle last:border-b-0',
                      onRowClick && 'cursor-pointer hover:bg-surface-subtle',
                      (selected || isActive) && 'bg-accent-selected hover:bg-accent-selected',
                      loading && 'opacity-60',
                    )}
                  >
                    {selection ? (
                      <td className={cellPad} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          aria-label="Seleccionar fila"
                          checked={selected}
                          onCheckedChange={(c) => {
                            const next = new Set(selection.selectedIds);
                            if (c === true) next.add(id);
                            else next.delete(id);
                            selection.onChange(next);
                          }}
                        />
                      </td>
                    ) : null}
                    {columns.map((col, ci) => {
                      const align = col.align ?? (col.numeric ? 'right' : 'left');
                      const content = col.cell(row);
                      return (
                        <td
                          key={col.id}
                          className={cn(
                            cellPad,
                            'align-middle text-text-primary',
                            col.numeric && 'ef-tabular',
                            align === 'right' && 'text-right',
                            align === 'center' && 'text-center',
                            mobileHide(col),
                            col.className,
                          )}
                        >
                          {ci === 0 && onRowClick ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRowClick(row);
                              }}
                              aria-label={rowActionLabel?.(row)}
                              className="max-w-full rounded-[4px] text-left font-medium text-text-primary hover:underline focus-visible:outline-2 focus-visible:outline-focus"
                            >
                              {content}
                            </button>
                          ) : (
                            content
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {footer ? <div className="mt-3">{footer}</div> : null}
    </div>
  );
}

export interface PaginationProps {
  /** e.g. "Mostrando 25 de 312". Unknown totals are fine: omit it. */
  summary?: React.ReactNode;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (size: number) => void;
  loading?: boolean;
  className?: string;
}

/** Cursor-style pagination: previous / next plus page size. */
export function Pagination({
  summary,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  pageSize,
  pageSizeOptions = [25, 50, 100],
  onPageSizeChange,
  loading,
  className,
}: PaginationProps) {
  return (
    <nav aria-label="Paginación" className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <p className="ef-tabular text-meta text-text-secondary" aria-live="polite">
        {summary}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {onPageSizeChange && pageSize ? (
          <div className="flex items-center gap-2">
            <span className="text-meta text-text-secondary" aria-hidden>
              Filas por página
            </span>
            <Select
              aria-label="Filas por página"
              className="h-8 w-20"
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
              options={pageSizeOptions.map((n) => ({ value: String(n), label: String(n) }))}
            />
          </div>
        ) : null}
        <Button variant="secondary" size="sm" onClick={onPrevious} disabled={!hasPrevious || loading} leadingIcon={<Icon icon={ArrowLeft01Icon} aria-hidden />}>
          Anterior
        </Button>
        <Button variant="secondary" size="sm" onClick={onNext} disabled={!hasNext || loading}>
          Siguiente
          <Icon icon={ArrowRight01Icon} aria-hidden />
        </Button>
      </div>
    </nav>
  );
}
